import React, { useEffect, useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { merchantApi } from '../api/config';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUniversity, faChartBar, faRightFromBracket,
  faBars, faTimes, faUser, faMoneyBillTransfer
} from '@fortawesome/free-solid-svg-icons';

interface MenuItem {
  path: string;
  icon: any;
  label: string;
  exact?: boolean;
}

const menuGroups: { group: string | null; items: MenuItem[] }[] = [
  {
    group: null,
    items: [
      { path: '/agent', icon: faChartBar, label: 'รายงานค่าคอม', exact: true },
      { path: '/agent/transactions', icon: faMoneyBillTransfer, label: 'Transactions' },
    ],
  },
];

const AgentLayout: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [agentUser, setAgentUser] = useState<any>(null);
  const [systemLogo, setSystemLogo] = useState<string | null>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' ? window.innerWidth >= 1024 : true
  );

  useEffect(() => {
    const stored = localStorage.getItem('agent_user');
    if (stored) setAgentUser(JSON.parse(stored));
    loadSystemLogo();
  }, []);

  const loadSystemLogo = async () => {
    try {
      const response = await merchantApi.get('/admin/settings');
      const settings = response.data.data || [];
      const logoSetting = settings.find((s: any) => s.setting_key === 'system_logo_url');
      if (logoSetting?.setting_value) setSystemLogo(logoSetting.setting_value);
    } catch {}
  };

  useEffect(() => {
    const handleResize = () => setIsDesktop(window.innerWidth >= 1024);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setMobileMenuOpen(false);
      document.body.style.overflow = '';
      return;
    }
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isDesktop, mobileMenuOpen]);

  const isCompact = isDesktop && sidebarCollapsed;

  const closeMobileMenu = () => {
    if (!isDesktop) setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    closeMobileMenu();
    try { await merchantApi.post('/agent/auth/logout'); } catch {}
    localStorage.removeItem('agent_user');
    navigate('/agent/login');
  };

  return (
    <div className="flex min-h-screen bg-slate-100">
      {/* Mobile overlay */}
      <div
        className={`fixed inset-0 z-30 bg-slate-950/50 backdrop-blur-sm transition-opacity duration-300 lg:hidden ${
          mobileMenuOpen ? 'opacity-100' : 'pointer-events-none opacity-0'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Sidebar */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-40 flex flex-col bg-white shadow-2xl
          transition-transform duration-300 ease-in-out lg:transition-all lg:shadow-none
          lg:border-r lg:border-gray-200/70
          ${isCompact ? 'lg:w-20' : 'lg:w-72'}
          w-72
          ${mobileMenuOpen || isDesktop ? 'translate-x-0' : '-translate-x-full'}
        `}
      >
        {/* Top gradient accent strip */}
        <div className="h-[3px] bg-gradient-primary flex-shrink-0" />

        {/* Header */}
        <div
          className={`relative px-4 pt-5 pb-4 ${isCompact ? 'text-center' : ''}`}
          style={{ borderBottom: '1px solid #f1f5f9' }}
        >
          {/* Toggle desktop */}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="absolute top-[18px] right-3 hidden h-7 w-7 items-center justify-center
                       rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       transition-all duration-200 lg:flex"
          >
            <FontAwesomeIcon icon={sidebarCollapsed ? faBars : faTimes} className="text-xs" />
          </button>

          {/* Close mobile */}
          <button
            type="button"
            onClick={closeMobileMenu}
            className="absolute top-[18px] right-3 flex h-7 w-7 items-center justify-center rounded-lg
                       text-gray-400 hover:text-gray-600 hover:bg-gray-100
                       transition-all duration-200 lg:hidden"
          >
            <FontAwesomeIcon icon={faTimes} className="text-xs" />
          </button>

          {!isCompact ? (
            <>
              {/* Brand / Logo */}
              {systemLogo ? (
                <div className="mb-4">
                  <img
                    src={systemLogo}
                    alt="Logo"
                    className="max-h-14 w-auto max-w-full object-contain"
                  />
                  <p className="text-[9px] font-medium uppercase tracking-[0.15em] mt-2 text-gray-400">
                    Agent Portal
                  </p>
                </div>
              ) : (
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 text-white"
                    style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
                  >
                    <FontAwesomeIcon icon={faUniversity} className="text-sm" />
                  </div>
                  <div>
                    <h1 className="text-gray-800 font-semibold text-[15px] tracking-tight leading-none">Agent Portal</h1>
                    <p className="text-[9px] font-medium uppercase tracking-[0.15em] mt-[3px] text-gray-400">
                      Commission Report
                    </p>
                  </div>
                </div>
              )}

              {/* User Info */}
              {agentUser && (
                <div className="rounded-xl overflow-hidden bg-gray-50 border border-gray-100/80">
                  <div className="h-[2px] bg-gradient-primary" />
                  <div className="flex items-center gap-2.5 p-2.5">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-xs flex-shrink-0"
                         style={{ background: 'var(--theme-primary-gradient)' }}>
                      <FontAwesomeIcon icon={faUser} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate leading-none mb-[5px] text-gray-700">
                        {agentUser.name || agentUser.email}
                      </p>
                      <p className="text-[10px] flex items-center gap-1.5 leading-none text-gray-400">
                        <span className="w-1.5 h-1.5 rounded-full animate-pulse flex-shrink-0 bg-emerald-400" />
                        Agent Bank
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div
              className="w-9 h-9 mx-auto rounded-xl flex items-center justify-center text-white mt-5"
              style={{ background: 'var(--theme-primary-gradient)', boxShadow: 'var(--theme-shadow-primary)' }}
            >
              <FontAwesomeIcon icon={faUniversity} className="text-sm" />
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-2.5 py-3 overflow-y-auto custom-scrollbar-light">
          <div className="space-y-4">
            {menuGroups.map((group, gi) => (
              <div key={gi}>
                {group.group && !isCompact && (
                  <div className="flex items-center gap-2 px-1 mb-2">
                    <div className="w-3 h-[1.5px] rounded-full flex-shrink-0" style={{ background: 'var(--theme-primary)', opacity: 0.5 }} />
                    <p className="text-[9.5px] font-bold uppercase tracking-[0.12em] leading-none text-gray-400">
                      {group.group}
                    </p>
                    <div className="flex-1 h-[1px] rounded-full bg-gray-100" />
                  </div>
                )}
                {group.group && isCompact && (
                  <div className="mx-3 mb-2 h-[1px] bg-gray-100" />
                )}

                <div className="space-y-[2px]">
                  {group.items.map(item => {
                    const isActive = item.exact
                      ? location.pathname === item.path
                      : location.pathname.startsWith(item.path);

                    return (
                      <Link
                        key={item.path}
                        to={item.path}
                        title={isCompact ? item.label : undefined}
                        onClick={closeMobileMenu}
                        className={`
                          flex items-center gap-3 py-2.5 rounded-xl text-sm relative group
                          transition-all duration-200
                          ${isCompact ? 'justify-center px-0' : 'pl-3.5 pr-3'}
                          ${isActive
                            ? 'font-medium text-white'
                            : 'font-normal text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                          }
                        `}
                        style={isActive ? {
                          background: 'var(--theme-primary-gradient)',
                          boxShadow: 'var(--theme-shadow-primary)',
                        } : undefined}
                      >
                        <span className={`text-[13px] flex-shrink-0 transition-colors duration-200 ${
                          isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'
                        }`}>
                          <FontAwesomeIcon icon={item.icon} />
                        </span>

                        {!isCompact && <span className="truncate">{item.label}</span>}

                        {isCompact && (
                          <div className="absolute left-full ml-3 px-3 py-1.5 text-gray-700 bg-white text-xs rounded-lg
                                         opacity-0 invisible group-hover:opacity-100 group-hover:visible
                                         transition-all duration-200 whitespace-nowrap z-50 shadow-lg border border-gray-200">
                            {item.label}
                            <div className="absolute left-0 top-1/2 -translate-x-[7px] -translate-y-1/2 border-[5px] border-transparent border-r-white" />
                          </div>
                        )}
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </nav>

        {/* Commission rates info */}
        {!isCompact && agentUser && (
          <div className="px-4 pb-3">
            <div className="rounded-xl p-3 border border-gray-100 bg-gray-50/80">
              <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider mb-2">อัตราค่าคอม</p>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg px-2.5 py-2 bg-green-50 border border-green-100/80">
                  <p className="text-[9px] text-green-600 font-semibold uppercase tracking-wider leading-none mb-1">ฝาก</p>
                  <p className="text-sm font-bold text-green-700">{agentUser.deposit_commission_rate ?? 0}%</p>
                </div>
                <div className="rounded-lg px-2.5 py-2 bg-amber-50 border border-amber-100/80">
                  <p className="text-[9px] text-amber-600 font-semibold uppercase tracking-wider leading-none mb-1">ถอน</p>
                  <p className="text-sm font-bold text-amber-700">{agentUser.withdrawal_commission_rate ?? 0}%</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Logout */}
        <div className="px-2.5 pb-3 pt-2 border-t border-gray-100">
          <button
            onClick={handleLogout}
            title={isCompact ? 'ออกจากระบบ' : undefined}
            className={`
              w-full py-2.5 px-3.5 rounded-xl text-sm font-medium
              flex items-center gap-3 transition-all duration-200 cursor-pointer
              border border-gray-200 bg-gray-50 text-gray-400
              hover:text-red-500 hover:bg-red-50 hover:border-red-200
              ${isCompact ? 'justify-center' : ''}
            `}
          >
            <FontAwesomeIcon icon={faRightFromBracket} />
            {!isCompact && <span>ออกจากระบบ</span>}
          </button>

          {!isCompact && (
            <p className="text-center text-[9.5px] mt-2.5 tracking-wide text-gray-300">
              Agent Portal
            </p>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className={`
        ml-0 flex-1 min-w-0 min-h-screen overflow-x-clip transition-all duration-300
        ${isCompact ? 'lg:ml-20' : 'lg:ml-72'}
      `}>
        {/* Top Header Bar — ใช้ overflow-x-clip บน main (ไม่ใช่ hidden) ไม่งั้น sticky ไม่ยึด viewport */}
        <div className="sticky top-0 z-20 flex items-center gap-3 border-b border-gray-200/60 bg-white/80 px-4 py-3 backdrop-blur-md sm:px-6">
          <button
            type="button"
            onClick={() => setMobileMenuOpen(true)}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-gray-200/70 text-gray-600 transition-colors hover:bg-gray-50 lg:hidden"
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          {/* Breadcrumb / Page Title */}
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <FontAwesomeIcon icon={faUniversity} className="text-xs" style={{ color: 'var(--theme-primary)' }} />
            <span className="font-medium text-gray-700">Agent Portal</span>
          </div>

          {/* Agent name badge (right) */}
          {agentUser && (
            <div className="ml-auto flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200/80 bg-white/70">
              <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
              <span className="text-xs font-medium text-gray-600">{agentUser.name || agentUser.email}</span>
            </div>
          )}
        </div>

        <div className="p-4 sm:p-6">
          <Outlet />
        </div>
      </main>

      <style>{`
        .custom-scrollbar-light::-webkit-scrollbar { width: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb { background: #e5e7eb; border-radius: 4px; }
        .custom-scrollbar-light::-webkit-scrollbar-thumb:hover { background: #d1d5db; }
      `}</style>
    </div>
  );
};

export default AgentLayout;
